import {
  type FeedbackForm,
  type FeedbackFormField,
  type FeedbackFormFieldKind,
  type FeedbackFormFieldsInput,
  type FeedbackFormSubmission,
  type FormFieldSummary,
  type FormSentimentSummary,
  type FormSubmissionSentiment,
  type GameSentimentSummaryRow,
  FeedbackFormFieldRowSchema,
  FeedbackFormRowSchema,
  FeedbackFormSubmissionRowSchema,
  FormFieldSummaryRowSchema,
  FormSentimentSummaryRowSchema,
  FormSubmissionSentimentRowSchema,
  GameSentimentSummaryRowSchema,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import {
  aggregateReactions,
  type ReactionEvent,
  type ReactionTotal,
} from '../feedback/reaction-aggregate.js';

// ---------------------------------------------------------------------------
// Feedback Forms data layer.
//
// Drafts-only surface for PR 3 of the epic. Reads go through `from()` since
// RLS already restricts rows to the caller's studio; writes go through the
// security-definer RPCs introduced in migration 0029 so the per-kind config
// validation lives in one place. The Realtime subscription that keeps the
// list fresh is owned by ShellProvider (one channel per active game), not
// here - this file is just thin RPC wrappers + row -> camelCase mapping.
// ---------------------------------------------------------------------------

const FORMS_TABLE = 'feedback_forms';
const FIELDS_TABLE = 'feedback_form_fields';

function parseForm(raw: unknown): FeedbackForm | null {
  const parsed = FeedbackFormRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[feedback-forms] Could not parse form row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

function parseField(raw: unknown): FeedbackFormField | null {
  const parsed = FeedbackFormFieldRowSchema.safeParse(raw);
  if (!parsed.success) {
    console.warn('[feedback-forms] Could not parse field row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

// Forms list rows carry a submission count alongside the parsed form so
// the row component can preemptively gate the Delete affordance without
// fanning out a count query per row. The count comes from PostgREST
// aggregation in the same select; zero submissions is the common case.
export interface FeedbackFormWithCount {
  form: FeedbackForm;
  submissionsCount: number;
}

export interface FetchFeedbackFormsOptions {
  // Default false. The Forms list page passes true so the
  // show-archived toggle has rows to surface; the shell context leaves
  // it unset so archived forms don't leak into other surfaces (e.g. the
  // starter-form banner check).
  includeArchived?: boolean;
}

export async function fetchFeedbackFormsForGame(
  gameId: string,
  options: FetchFeedbackFormsOptions = {},
): Promise<FeedbackFormWithCount[]> {
  let query = supabase
    .from(FORMS_TABLE)
    .select('*, feedback_form_submissions(count)')
    .eq('game_id', gameId)
    .order('created_at', { ascending: false });

  if (options.includeArchived !== true) {
    query = query.is('archived_at', null);
  }

  const { data, error } = await query;

  if (error !== null) {
    console.warn('[feedback-forms] Failed to load forms:', error.message);
    return [];
  }

  const forms: FeedbackFormWithCount[] = [];
  for (const raw of data ?? []) {
    const row = raw as { feedback_form_submissions?: Array<{ count: number }> };
    const parsed = parseForm(raw);
    if (parsed === null) continue;
    const submissionsCount = row.feedback_form_submissions?.[0]?.count ?? 0;
    forms.push({ form: parsed, submissionsCount });
  }
  return forms;
}

export async function fetchFeedbackForm(formId: string): Promise<FeedbackForm | null> {
  const { data, error } = await supabase
    .from(FORMS_TABLE)
    .select('*')
    .eq('id', formId)
    .maybeSingle();

  if (error !== null) {
    console.warn('[feedback-forms] Failed to load form:', error.message);
    return null;
  }
  if (data === null) return null;
  return parseForm(data);
}

export async function fetchFeedbackFormFields(
  formId: string,
): Promise<FeedbackFormField[]> {
  const { data, error } = await supabase
    .from(FIELDS_TABLE)
    .select('*')
    .eq('form_id', formId)
    .order('order_index', { ascending: true });

  if (error !== null) {
    console.warn('[feedback-forms] Failed to load fields:', error.message);
    return [];
  }

  const fields: FeedbackFormField[] = [];
  for (const raw of data ?? []) {
    const parsed = parseField(raw);
    if (parsed !== null) fields.push(parsed);
  }
  return fields;
}

// RPC error tokens raised by 0029 + 0032. The portal maps these to
// user-facing copy at the call site rather than here so the messaging
// stays close to the UI.
export type FeedbackFormRpcError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'form_not_found'
  | 'name_required'
  | 'name_too_long'
  | 'title_required'
  | 'title_too_long'
  | 'invalid_repost_frequency'
  | 'anonymous_required'
  | 'form_enabled_cannot_edit'
  | 'form_enabled_cannot_delete'
  | 'fields_must_be_array'
  | 'at_least_one_field_required'
  | 'too_many_fields'
  | 'invalid_field_kind'
  | 'field_title_required'
  | 'field_title_too_long'
  | 'field_description_too_long'
  | 'field_config_must_be_object'
  | 'rating_display_must_be_object'
  | 'rating_display_mode_invalid'
  | 'rating_display_emoji_invalid'
  | 'binary_label_must_be_string'
  | 'binary_label_too_long'
  | 'only_one_nps_field_per_form'
  | 'select_options_required'
  | 'select_needs_at_least_one_option'
  | 'select_too_many_options'
  | 'form_has_submissions'
  // 0032 lifecycle additions
  | 'form_already_enabled'
  | 'survey_channel_required'
  | 'response_channel_required'
  | 'submit_response_title_required'
  | 'fields_required'
  | 'permission_check_required'
  | 'no_channels_to_check'
  // 0034 repost additions
  | 'form_not_enabled'
  | 'repost_cooldown'
  // 0042 recruit additions
  | 'recruit_requires_connection'
  | 'recruit_unknown_channel_id'
  | 'recruit_unknown_role_id'
  | 'unknown';

export interface FeedbackFormRpcResult<T> {
  ok: boolean;
  data: T | null;
  error: FeedbackFormRpcError | null;
}

const KNOWN_ERROR_TOKENS = new Set<FeedbackFormRpcError>([
  'not_authenticated',
  'not_a_member',
  'game_not_found',
  'form_not_found',
  'name_required',
  'name_too_long',
  'title_required',
  'title_too_long',
  'invalid_repost_frequency',
  'anonymous_required',
  'form_enabled_cannot_edit',
  'form_enabled_cannot_delete',
  'fields_must_be_array',
  'at_least_one_field_required',
  'too_many_fields',
  'invalid_field_kind',
  'field_title_required',
  'field_title_too_long',
  'field_description_too_long',
  'field_config_must_be_object',
  'rating_display_must_be_object',
  'rating_display_mode_invalid',
  'rating_display_emoji_invalid',
  'binary_label_must_be_string',
  'binary_label_too_long',
  'only_one_nps_field_per_form',
  'select_options_required',
  'select_needs_at_least_one_option',
  'select_too_many_options',
  'form_has_submissions',
  'form_already_enabled',
  'survey_channel_required',
  'response_channel_required',
  'submit_response_title_required',
  'fields_required',
  'permission_check_required',
  'no_channels_to_check',
  'form_not_enabled',
  'repost_cooldown',
  'recruit_requires_connection',
  'recruit_unknown_channel_id',
  'recruit_unknown_role_id',
]);

function classifyError(message: string | undefined | null): FeedbackFormRpcError {
  if (typeof message !== 'string') return 'unknown';
  // Postgres exception messages come through as the raise exception text.
  for (const token of KNOWN_ERROR_TOKENS) {
    if (message.includes(token)) return token;
  }
  return 'unknown';
}

export async function createFeedbackForm(
  gameId: string,
  name: string,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('create_feedback_form', {
    p_game_id: gameId,
    p_name: name,
  });

  if (error !== null) {
    console.warn('[feedback-forms] create failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

export interface UpdateFeedbackFormPatch {
  name: string;
  internalDescription: string | null;
  title: string;
  discordDescription: string | null;
  surveyChannelId: string | null;
  responseChannelId: string | null;
  repostFrequency: 'never' | 'daily' | 'weekly' | 'monthly';
  anonymous: boolean;
  submitResponseTitle: string | null;
  submitResponseDescription: string | null;
  recruitChannelIds: string[];
  recruitRoleIds: string[];
}

export async function updateFeedbackForm(
  formId: string,
  patch: UpdateFeedbackFormPatch,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('update_feedback_form', {
    p_form_id: formId,
    p_name: patch.name,
    p_internal_description: patch.internalDescription,
    p_title: patch.title,
    p_discord_description: patch.discordDescription,
    p_survey_channel_id: patch.surveyChannelId,
    p_response_channel_id: patch.responseChannelId,
    p_repost_frequency: patch.repostFrequency,
    p_anonymous: patch.anonymous,
    p_submit_response_title: patch.submitResponseTitle,
    p_submit_response_description: patch.submitResponseDescription,
    p_recruit_channel_ids: patch.recruitChannelIds,
    p_recruit_role_ids: patch.recruitRoleIds,
  });

  if (error !== null) {
    console.warn('[feedback-forms] update failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

// Builder hands the in-memory field list straight to the RPC. The RPC
// validates per-kind config server-side so the client only needs to ship
// the wire shape; spreading `{ kind, title, description, required, config }`
// matches what 0029 reads via jsonb_array_elements.
export async function replaceFeedbackFormFields(
  formId: string,
  fields: FeedbackFormFieldsInput,
): Promise<FeedbackFormRpcResult<FeedbackFormField[]>> {
  const wire = fields.map((f) => {
    const base = {
      kind: f.kind,
      title: f.title,
      description: f.description ?? null,
      required: f.required,
    };
    if (f.kind === 'rating') {
      // No display key = numbers (the default). Only emit it when the
      // studio opted into emoji mode so the wire shape stays minimal.
      const config: { display?: { mode: 'emoji'; emoji: string } } = {};
      if (f.display?.mode === 'emoji') {
        config.display = { mode: 'emoji', emoji: f.display.emoji };
      }
      return { ...base, config };
    }
    if (f.kind === 'select') {
      const config: { options: string[]; maxValues?: number } = { options: f.options };
      if (f.maxValues !== undefined && f.maxValues > 1) {
        config.maxValues = f.maxValues;
      }
      return { ...base, config };
    }
    if (f.kind === 'binary') {
      // Override keys are flat on the input shape; nest them under config
      // so the RPC sees them where it validates and stores them.
      const config: { trueLabel?: string; falseLabel?: string } = {};
      if (f.trueLabel !== undefined) config.trueLabel = f.trueLabel;
      if (f.falseLabel !== undefined) config.falseLabel = f.falseLabel;
      return { ...base, config };
    }
    return { ...base, config: {} };
  });

  const { data, error } = await supabase.rpc('replace_feedback_form_fields', {
    p_form_id: formId,
    p_fields: wire,
  });

  if (error !== null) {
    console.warn('[feedback-forms] replace fields failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const out: FeedbackFormField[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = parseField(raw);
    if (parsed !== null) out.push(parsed);
  }
  return { ok: true, data: out, error: null };
}

export async function deleteFeedbackForm(
  formId: string,
): Promise<FeedbackFormRpcResult<null>> {
  const { error } = await supabase.rpc('delete_feedback_form', {
    p_form_id: formId,
  });

  if (error !== null) {
    console.warn('[feedback-forms] delete failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  return { ok: true, data: null, error: null };
}

// Lifecycle wrappers (PR 4) ----------------------------------------------
//
// Server-side mirrors of the readiness rules sit in 0032; the portal
// gates the Enable button optimistically so the user gets the right UX
// without round-tripping, but each RPC re-validates so a stale view can't
// flip a not-ready form live. requestFormPermissionCheck is fire-and-
// forget - the bot writes back to feedback_forms.last_perm_check_*, the
// portal sees the change via the existing Realtime subscription that
// ShellProvider holds.

export async function enableFeedbackForm(
  formId: string,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('enable_feedback_form', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] enable failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

export async function disableFeedbackForm(
  formId: string,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('disable_feedback_form', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] disable failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

// Archive lifecycle (migration 0047) -------------------------------------
//
// Archive hides a form from the default list and disables it as a side
// effect (the RPC runs the disable teardown so the bot pulls the posted
// button-message). Unarchive just nulls archived_at and leaves status
// alone - the studio re-enables explicitly if they want it live again.

export async function archiveFeedbackForm(
  formId: string,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('archive_feedback_form', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] archive failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

export async function unarchiveFeedbackForm(
  formId: string,
): Promise<FeedbackFormRpcResult<FeedbackForm>> {
  const { data, error } = await supabase.rpc('unarchive_feedback_form', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] unarchive failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  const parsed = parseForm(data);
  if (parsed === null) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed, error: null };
}

export async function requestFormPermissionCheck(
  formId: string,
): Promise<FeedbackFormRpcResult<null>> {
  const { error } = await supabase.rpc('request_form_permission_check', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] perm check request failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  return { ok: true, data: null, error: null };
}

// Manual "Repost now" entry point (PR 6). Fire-and-forget from the
// portal's perspective: the bot picks the queue row up, posts a fresh
// button-message, and updates feedback_forms.last_reposted_at - which the
// existing Realtime subscription on the forms list already mirrors. The
// 30s cooldown lives in the RPC; the row also disables its button for the
// same window so the typical case never reaches the SQL guard.
export async function requestFormRepost(
  formId: string,
): Promise<FeedbackFormRpcResult<null>> {
  const { error } = await supabase.rpc('request_form_repost', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] repost request failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  return { ok: true, data: null, error: null };
}

// Submission read paths (PR 5) -------------------------------------------
//
// Two consumers:
//   * The Feedback inbox (Form submissions pane + activity feed entry).
//     Wants every submission for the active game, joined with form
//     metadata (title, survey channel) and the form's field list so the
//     card can render Q/A blocks without a second round trip.
//   * The per-form responses page. Same shape, but scoped to one form
//     and paginated `submitted_at desc` 50 at a time.
//
// We embed via PostgREST's relationship syntax. `feedback_forms!inner`
// turns the form into a semi-join (drops orphans defensively), and the
// nested `feedback_form_fields(...)` inlines the question text. RLS still
// scopes everything to the studio so the explicit eq() is a UX scope, not
// a security boundary.

export interface FormSubmissionFormSummary {
  id: string;
  // Portal label - used by inbox cards and submission rows.
  name: string;
  // Discord-facing title - kept for any surface that needs the name
  // the player saw in the modal.
  title: string;
  surveyChannelId: string | null;
}

export interface FormSubmissionFieldSummary {
  id: string;
  title: string;
  orderIndex: number;
  // Pulled from the current field row via the join. Absent when the
  // field has been deleted off the form between submission time and
  // now - the renderer falls back to the raw stored value in that case.
  kind?: FeedbackFormFieldKind;
  config?: Record<string, unknown>;
}

export interface EnrichedFeedbackFormSubmission {
  submission: FeedbackFormSubmission;
  form: FormSubmissionFormSummary;
  // Ordered by `orderIndex` ASC so the card can iterate without sorting.
  fields: FormSubmissionFieldSummary[];
  // Discord engagement on the summary embed (reactions / replies / thread
  // messages). Null when the submission's sibling Pool row has no
  // external_id yet (summary failed to post, or pre-migration submission).
  engagement: FormSubmissionEngagement | null;
}

// A reply or thread message attached to a form submission's summary post.
// Sourced from feedback_pool rows whose reply_to_external_id or thread_id
// matches the summary's external_id.
export interface SubmissionReply {
  id: string;
  externalId: string | null;
  authorHandle: string | null;
  authorExternalId: string | null;
  rawText: string;
  postedAt: string | null;
  isThreadMessage: boolean;
}

export interface FormSubmissionEngagement {
  // Identifier of the summary post in Discord; lets the UI deep-link out.
  summaryExternalId: string;
  reactions: ReactionTotal[];
  // Aggregated counts surfaced as compact badges on the submission card.
  // Threads only count replies *under* the summary (the starter message
  // itself isn't double-counted).
  replyCount: number;
  threadReplyCount: number;
  replies: SubmissionReply[];
  threadReplies: SubmissionReply[];
  // Max of the latest reaction add and the latest reply posted_at. Drives
  // the activity-feed sort key when engagement happens after submitTime.
  lastActivityAt: string | null;
}

const SUBMISSION_SELECT =
  'id, form_id, game_id, studio_id, feedback_pool_id, submitter_external_id, ' +
  'submitter_handle, values, field_snapshot, discord_interaction_id, submitted_at, ' +
  'feedback_forms!inner(id, name, title, survey_channel_id, ' +
    'feedback_form_fields(id, title, order_index, kind, config)' +
  ')';

interface RawJoinedSubmission {
  id: unknown;
  form_id: unknown;
  game_id: unknown;
  studio_id: unknown;
  feedback_pool_id: unknown;
  submitter_external_id: unknown;
  submitter_handle: unknown;
  values: unknown;
  field_snapshot: unknown;
  discord_interaction_id: unknown;
  submitted_at: unknown;
  feedback_forms: unknown;
}

function parseEnrichedSubmission(
  raw: unknown,
): EnrichedFeedbackFormSubmission | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const row = raw as RawJoinedSubmission;
  const submission = FeedbackFormSubmissionRowSchema.safeParse({
    id: row.id,
    form_id: row.form_id,
    game_id: row.game_id,
    studio_id: row.studio_id,
    feedback_pool_id: row.feedback_pool_id,
    submitter_external_id: row.submitter_external_id,
    submitter_handle: row.submitter_handle,
    values: row.values,
    field_snapshot: row.field_snapshot,
    discord_interaction_id: row.discord_interaction_id,
    submitted_at: row.submitted_at,
  });
  if (!submission.success) {
    console.warn(
      '[feedback-forms] submission row parse failed:',
      submission.error.issues,
    );
    return null;
  }
  // PostgREST embeds 1:1 (`!inner`) as a plain object; keep the array
  // fallback so a subtle relationship-shape change doesn't crash here.
  const formRaw = Array.isArray(row.feedback_forms)
    ? row.feedback_forms[0]
    : row.feedback_forms;
  if (typeof formRaw !== 'object' || formRaw === null) return null;
  const form = formRaw as {
    id?: unknown;
    name?: unknown;
    title?: unknown;
    survey_channel_id?: unknown;
    feedback_form_fields?: unknown;
  };
  if (
    typeof form.id !== 'string' ||
    typeof form.name !== 'string' ||
    typeof form.title !== 'string' ||
    !(typeof form.survey_channel_id === 'string' || form.survey_channel_id === null)
  ) {
    return null;
  }
  // Prefer the field_snapshot captured at submission time. Migration
  // 0033 added it; older rows are NULL and fall back to the joined
  // current fields, which only render correctly if the form was never
  // edited after submission. Either way we also index the current
  // join's kind + config by field id so the renderer can resolve
  // canonical binary values to the studio's chosen labels.
  const currentFieldExtras = new Map<
    string,
    {
      kind: FeedbackFormFieldKind;
      config: Record<string, unknown>;
    }
  >();
  if (Array.isArray(form.feedback_form_fields)) {
    for (const f of form.feedback_form_fields) {
      if (typeof f !== 'object' || f === null) continue;
      const field = f as {
        id?: unknown;
        kind?: unknown;
        config?: unknown;
      };
      if (typeof field.id !== 'string' || typeof field.kind !== 'string') continue;
      const config =
        typeof field.config === 'object' && field.config !== null
          ? (field.config as Record<string, unknown>)
          : {};
      currentFieldExtras.set(field.id, {
        kind: field.kind as FeedbackFormFieldKind,
        config,
      });
    }
  }

  const fields: FormSubmissionFieldSummary[] = [];
  const snapshot = submission.data.fieldSnapshot;
  if (snapshot !== null) {
    for (const entry of snapshot) {
      const extras = currentFieldExtras.get(entry.id);
      fields.push({
        id: entry.id,
        title: entry.title,
        orderIndex: entry.orderIndex,
        ...(extras !== undefined ? { kind: extras.kind, config: extras.config } : {}),
      });
    }
    fields.sort((a, b) => a.orderIndex - b.orderIndex);
  } else if (Array.isArray(form.feedback_form_fields)) {
    for (const f of form.feedback_form_fields) {
      if (typeof f !== 'object' || f === null) continue;
      const field = f as {
        id?: unknown;
        title?: unknown;
        order_index?: unknown;
      };
      if (
        typeof field.id !== 'string' ||
        typeof field.title !== 'string' ||
        typeof field.order_index !== 'number'
      ) {
        continue;
      }
      const extras = currentFieldExtras.get(field.id);
      fields.push({
        id: field.id,
        title: field.title,
        orderIndex: field.order_index,
        ...(extras !== undefined ? { kind: extras.kind, config: extras.config } : {}),
      });
    }
    fields.sort((a, b) => a.orderIndex - b.orderIndex);
  }
  return {
    submission: submission.data,
    form: {
      id: form.id,
      name: form.name,
      title: form.title,
      surveyChannelId: form.survey_channel_id,
    },
    fields,
    engagement: null,
  };
}

// ---------------------------------------------------------------------------
// Engagement hydration.
//
// Each submission's sibling feedback_pool row carries the Discord summary
// message_id once the bot's writeback RPC has run. We need three pieces:
//
//   1. external_id per submission (= summary message snowflake)
//   2. feedback_reaction events keyed by feedback_id (the sibling pool id)
//   3. reply / thread messages: feedback_pool rows whose
//      reply_to_external_id or thread_id matches a summary external_id
//
// Done as a second pass keyed by the first page's pool ids - avoids
// PostgREST FK ambiguity between feedback_form_submissions.feedback_pool_id
// and feedback_pool.form_submission_id and keeps the two reply queries
// independent so a slow reply lookup doesn't stall the reaction join.
// ---------------------------------------------------------------------------

interface PoolEngagementRow {
  id: string;
  external_id: string | null;
  feedback_reaction: ReactionEvent[] | null;
}

interface ReplyOrThreadRow {
  id: string;
  external_id: string | null;
  author_handle: string | null;
  author_external_id: string | null;
  raw_text: string;
  posted_at: string | null;
  reply_to_external_id: string | null;
  thread_id: string | null;
}

export async function fetchSubmissionEngagementMap(
  submissions: EnrichedFeedbackFormSubmission[],
): Promise<Map<string, FormSubmissionEngagement>> {
  const out = new Map<string, FormSubmissionEngagement>();
  const poolIds: string[] = [];
  const poolIdToSubmissionId = new Map<string, string>();
  for (const s of submissions) {
    const poolId = s.submission.feedbackPoolId;
    if (poolId === null) continue;
    poolIds.push(poolId);
    poolIdToSubmissionId.set(poolId, s.submission.id);
  }
  if (poolIds.length === 0) return out;

  const { data: poolRows, error: poolErr } = await supabase
    .from('feedback_pool')
    .select(
      'id, external_id, feedback_reaction(emoji, action, observed_at, reactor_external_id)',
    )
    .in('id', poolIds);
  if (poolErr !== null) {
    console.warn('[feedback-forms] submission engagement load failed:', poolErr.message);
    return out;
  }

  // Build summary external_id -> submissionId for the reply / thread
  // query, and seed engagement objects keyed by submission id.
  const externalIdToSubmissionId = new Map<string, string>();
  const summaryExternalIds: string[] = [];
  for (const row of (poolRows ?? []) as PoolEngagementRow[]) {
    const submissionId = poolIdToSubmissionId.get(row.id);
    if (submissionId === undefined) continue;
    if (row.external_id === null) continue;
    externalIdToSubmissionId.set(row.external_id, submissionId);
    summaryExternalIds.push(row.external_id);
    const events = row.feedback_reaction ?? [];
    const aggregate = aggregateReactions(events);
    out.set(submissionId, {
      summaryExternalId: row.external_id,
      reactions: aggregate.totals,
      replyCount: 0,
      threadReplyCount: 0,
      replies: [],
      threadReplies: [],
      lastActivityAt: aggregate.latestAddObservedAt,
    });
  }

  if (summaryExternalIds.length === 0) return out;

  // Replies + thread messages share the same source = 'discord_channel'
  // table. PostgREST `.or()` over two list filters returns the union; we
  // bucket each row into replies vs threadReplies in TS based on which
  // column matched.
  const externalIdListCsv = summaryExternalIds.map((id) => `"${id}"`).join(',');
  const { data: replyRows, error: replyErr } = await supabase
    .from('feedback_pool')
    .select(
      'id, external_id, author_handle, author_external_id, raw_text, posted_at, reply_to_external_id, thread_id',
    )
    .eq('source', 'discord_channel')
    .or(
      `reply_to_external_id.in.(${externalIdListCsv}),thread_id.in.(${externalIdListCsv})`,
    )
    .order('posted_at', { ascending: true });
  if (replyErr !== null) {
    console.warn('[feedback-forms] submission replies load failed:', replyErr.message);
    return out;
  }

  for (const row of (replyRows ?? []) as ReplyOrThreadRow[]) {
    // Prefer the explicit reply_to_external_id match; fall back to
    // thread_id. A reply *inside* a thread under a summary will set both,
    // and we want it counted as a reply (the more specific relationship)
    // rather than a generic thread message.
    let submissionId: string | undefined;
    let isThreadMessage = false;
    if (
      row.reply_to_external_id !== null
      && externalIdToSubmissionId.has(row.reply_to_external_id)
    ) {
      submissionId = externalIdToSubmissionId.get(row.reply_to_external_id);
    } else if (
      row.thread_id !== null
      && externalIdToSubmissionId.has(row.thread_id)
    ) {
      submissionId = externalIdToSubmissionId.get(row.thread_id);
      isThreadMessage = true;
    }
    if (submissionId === undefined) continue;
    const entry = out.get(submissionId);
    if (entry === undefined) continue;
    const reply: SubmissionReply = {
      id: row.id,
      externalId: row.external_id,
      authorHandle: row.author_handle,
      authorExternalId: row.author_external_id,
      rawText: row.raw_text,
      postedAt: row.posted_at,
      isThreadMessage,
    };
    if (isThreadMessage) {
      entry.threadReplies.push(reply);
      entry.threadReplyCount += 1;
    } else {
      entry.replies.push(reply);
      entry.replyCount += 1;
    }
    if (row.posted_at !== null) {
      if (entry.lastActivityAt === null || row.posted_at > entry.lastActivityAt) {
        entry.lastActivityAt = row.posted_at;
      }
    }
  }

  return out;
}

async function hydrateSubmissionsWithEngagement(
  submissions: EnrichedFeedbackFormSubmission[],
): Promise<EnrichedFeedbackFormSubmission[]> {
  if (submissions.length === 0) return submissions;
  const map = await fetchSubmissionEngagementMap(submissions);
  for (const s of submissions) {
    const engagement = map.get(s.submission.id);
    if (engagement !== undefined) s.engagement = engagement;
  }
  return submissions;
}

// Inbox-side fetch. Cursor-paginated newest-first so a busy game
// doesn't pay for hundreds of rows on first paint. The hook keeps a
// realtime subscription on top; new inserts prepend without
// advancing the cursor, so loadMore continues to walk older rows.
export const INBOX_SUBMISSION_PAGE_SIZE = 50;

export async function fetchFeedbackInboxSubmissions(
  gameId: string,
  opts: FetchFormSubmissionsOptions = {},
): Promise<EnrichedFeedbackFormSubmission[]> {
  const limit = opts.limit ?? INBOX_SUBMISSION_PAGE_SIZE;
  let query = supabase
    .from('feedback_form_submissions')
    .select(SUBMISSION_SELECT)
    .eq('game_id', gameId)
    .order('submitted_at', { ascending: false })
    .limit(limit);
  if (Array.isArray(opts.channelIds)) {
    // Filter on the embedded (!inner) form's survey channel so the inbox
    // can scope to the watched-channel set. An empty array matches nothing.
    query = query.in('feedback_forms.survey_channel_id', opts.channelIds);
  }
  if (typeof opts.before === 'string') {
    query = query.lt('submitted_at', opts.before);
  }
  const { data, error } = await query;
  if (error !== null) {
    console.warn('[feedback-forms] inbox submissions load failed:', error.message);
    return [];
  }
  const out: EnrichedFeedbackFormSubmission[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = parseEnrichedSubmission(raw);
    if (parsed !== null) out.push(parsed);
  }
  return hydrateSubmissionsWithEngagement(out);
}

export async function fetchFeedbackFormSubmissionById(
  submissionId: string,
): Promise<EnrichedFeedbackFormSubmission | null> {
  const { data, error } = await supabase
    .from('feedback_form_submissions')
    .select(SUBMISSION_SELECT)
    .eq('id', submissionId)
    .maybeSingle();
  if (error !== null) {
    console.warn('[feedback-forms] submission fetch failed:', error.message);
    return null;
  }
  if (data === null) return null;
  const parsed = parseEnrichedSubmission(data);
  if (parsed === null) return null;
  await hydrateSubmissionsWithEngagement([parsed]);
  return parsed;
}

export interface FetchFormSubmissionsOptions {
  limit?: number;
  // Cursor: pull rows older than this `submitted_at`. Null = first page.
  before?: string | null;
  // Numbered-pager mode. Mutually exclusive with `before`; when set,
  // queries with `.range(offset, offset + limit - 1)` so the responses
  // page can jump to an arbitrary page directly.
  offset?: number;
  // Restrict to submissions whose form's survey channel is in this set
  // (the inbox passes the watched-channel snowflakes so "All channels"
  // never surfaces forms from channels the studio no longer watches).
  // Omit for no channel scoping; an empty array matches nothing.
  channelIds?: string[];
}

export const FORM_RESPONSES_PAGE_SIZE = 50;

// Total submission count for one form. Backs the numbered pager on the
// per-form responses page; cheap (single COUNT) so it can re-fire when
// realtime nudges the responses page.
export async function getFeedbackFormSubmissionsCount(
  formId: string,
): Promise<number> {
  const { count, error } = await supabase
    .from('feedback_form_submissions')
    .select('id', { count: 'exact', head: true })
    .eq('form_id', formId);
  if (error !== null) {
    console.warn('[feedback-forms] submission count failed:', error.message);
    return 0;
  }
  return count ?? 0;
}

export async function fetchFeedbackFormSubmissions(
  formId: string,
  opts: FetchFormSubmissionsOptions = {},
): Promise<EnrichedFeedbackFormSubmission[]> {
  const limit = opts.limit ?? FORM_RESPONSES_PAGE_SIZE;
  let query = supabase
    .from('feedback_form_submissions')
    .select(SUBMISSION_SELECT)
    .eq('form_id', formId)
    .order('submitted_at', { ascending: false });
  // Numbered-pager mode wins over cursor mode when both are set; the
  // responses page uses offset, the inbox uses cursor.
  if (typeof opts.offset === 'number') {
    query = query.range(opts.offset, opts.offset + limit - 1);
  } else {
    query = query.limit(limit);
    if (typeof opts.before === 'string') {
      query = query.lt('submitted_at', opts.before);
    }
  }
  const { data, error } = await query;
  if (error !== null) {
    console.warn('[feedback-forms] form submissions load failed:', error.message);
    return [];
  }
  const out: EnrichedFeedbackFormSubmission[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = parseEnrichedSubmission(raw);
    if (parsed !== null) out.push(parsed);
  }
  return hydrateSubmissionsWithEngagement(out);
}

// ---------------------------------------------------------------------------
// Derived submission sentiment (migration 0106).
//
// Read-side aggregates of the existing per-record sentiment enrichments,
// fetched alongside the direct table reads above (not woven into them) via
// dedicated security-definer RPCs. The inbox fetches a whole page's
// submission sentiments in one call and merges by submission id, so cards
// never N+1.
// ---------------------------------------------------------------------------

// Per-submission sentiment for a page of submission ids. Returns a Map
// keyed by submission id; ids with no derived sentiment are simply absent.
export async function fetchFormSubmissionSentiments(
  submissionIds: string[],
): Promise<Map<string, FormSubmissionSentiment>> {
  const out = new Map<string, FormSubmissionSentiment>();
  if (submissionIds.length === 0) return out;
  const { data, error } = await supabase.rpc('get_form_submission_sentiments', {
    p_submission_ids: submissionIds,
  });
  if (error !== null) {
    console.warn('[feedback-forms] submission sentiments load failed:', error.message);
    return out;
  }
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = FormSubmissionSentimentRowSchema.safeParse(raw);
    if (parsed.success) out.set(parsed.data.submissionId, parsed.data);
  }
  return out;
}

// Game-wide sentiment summary (migration 0111). One row per source with
// scored records (discord_channel / form / poll; playtests excluded); the
// Community Feedback summary sums the rows for its active source filter.
// Returns [] on any failure so the summary degrades to empty.
export async function fetchGameSentimentSummary(
  gameId: string,
): Promise<GameSentimentSummaryRow[]> {
  const { data, error } = await supabase.rpc('get_game_sentiment_summary', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] game sentiment summary load failed:', error.message);
    return [];
  }
  const out: GameSentimentSummaryRow[] = [];
  for (const raw of (data ?? []) as unknown[]) {
    const parsed = GameSentimentSummaryRowSchema.safeParse(raw);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

// Form-level sentiment aggregate. Available for the future quantitative
// forms surface; no page consumes it yet. Returns null on any failure.
export async function fetchFormSentimentSummary(
  formId: string,
): Promise<FormSentimentSummary | null> {
  const { data, error } = await supabase.rpc('get_form_sentiment_summary', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] form sentiment summary load failed:', error.message);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) return null;
  const parsed = FormSentimentSummaryRowSchema.safeParse(row);
  if (!parsed.success) {
    console.warn('[feedback-forms] form sentiment summary parse failed:', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

// Per-field quantitative summary for one form (migration 0108): a series per
// question with the right aggregate for its kind (rating/nps distribution,
// select/severity counts, binary split, open-text response count). Returns
// null on any failure so the page simply hides the summary block.
export async function fetchFormFieldSummary(
  formId: string,
): Promise<FormFieldSummary | null> {
  const { data, error } = await supabase.rpc('get_form_field_summary', {
    p_form_id: formId,
  });
  if (error !== null) {
    console.warn('[feedback-forms] form field summary load failed:', error.message);
    return null;
  }
  // get_form_field_summary returns a single jsonb document; supabase-js may
  // hand it back directly or wrapped in a one-element array depending on the
  // function shape, so unwrap defensively as fetchFormSentimentSummary does.
  const row = Array.isArray(data) ? data[0] : data;
  if (row === undefined || row === null) return null;
  const parsed = FormFieldSummaryRowSchema.safeParse(row);
  if (!parsed.success) {
    console.warn('[feedback-forms] form field summary parse failed:', parsed.error.issues);
    return null;
  }
  return parsed.data;
}
